import { FormControl, FormGroup, Validators } from '@angular/forms';
import _ from 'lodash';
import { forkJoin, pairwise, Subject } from 'rxjs';
import { Area } from '../../../../models/area';
import { Brand, Region, Subregion } from '../../../../models/brand';
import { Dealer } from '../../../../models/dealer';
import { MeUser } from '../../../../models/me-user';
import { INetworkFilters, NetworkFilters } from '../../models/networkFilters';
import { brandTypeValidator } from '../../utils/brand-group.validator';
import { Network } from '../../../../services/payload/models/network';
import { SelectOption } from '../multi-select-autocomplete/multi-select-autocomplete.component';
import { FiltersApiService } from '../../services/filters.api.service';

export type INetworkFormGroup = {
  [P in keyof NetworkFilters]: FormControl<NetworkFilters[P]>;
};
export type NetworkFilterOptions = {
  [K in keyof NetworkFilters]: SelectOption[];
};

export class NetworkFiltersFormBuilder {
  private formReady: boolean = false;
  private _brands: Brand[] = [];
  private _user: MeUser = new MeUser({});
  private _dealers: Dealer[] = [];
  private _areaSales: Area[] = [];
  private _areaAfterSales: Area[] = [];
  applyNetworkFilters: Subject<void> = new Subject();
  options: NetworkFilterOptions = {
    brands: [],
    regions: [],
    subregions: [],
    markets: [],
    dealers: [],
    outlets: [],
    areaSales: [],
    subareaSales: [],
    areaAfterSales: [],
    subareaAfterSales: [],
  };

  form: FormGroup<INetworkFormGroup> = new FormGroup<INetworkFormGroup>({
    brands: new FormControl<string[]>([], { nonNullable: true }),
    regions: new FormControl<string[]>([], { nonNullable: true }),
    subregions: new FormControl<string[]>([], { nonNullable: true }),
    markets: new FormControl<string[]>([], { nonNullable: true }),
    areaSales: new FormControl<string[]>([], { nonNullable: true }),
    subareaSales: new FormControl<string[]>([], { nonNullable: true }),
    areaAfterSales: new FormControl<string[]>([], { nonNullable: true }),
    subareaAfterSales: new FormControl<string[]>([], { nonNullable: true }),
    dealers: new FormControl<string[]>([], { nonNullable: true }),
    outlets: new FormControl<string[]>([], { nonNullable: true }),
  });
  private availableFilters: (keyof INetworkFilters)[] = [];

  constructor(private apiService: FiltersApiService) {
    this.formSubscription();
  }

  updateAvailableFilters(filters: (keyof INetworkFilters)[]) {
    this.availableFilters = filters;
    this.filterOptions();
  }

  setInitialValues(user: MeUser, brands: Brand[]) {
    this._user = user;
    this._brands = brands;
    const networkFilters = new NetworkFilters(user);
    this.setValidators();
    if (!!user.market) {
      forkJoin({
        dealers: this.apiService.getDealers(networkFilters.markets[0], networkFilters.brands, networkFilters.areaSales, networkFilters.areaAfterSales, this._user.dealerGroup ?? null),
        areas: this.apiService.getAreas(networkFilters.markets[0], networkFilters.brands),
      }).subscribe(({ dealers, areas }) => {
        this.setDealerAndAreaOptions(dealers, areas.areaSales, areas.areaAfterSales);
        if (!!user.dealerGroup) networkFilters.dealers = this.options.dealers.map(x => x.value)
        if (!!user.dealer) networkFilters.dealers = [user.dealer];
        if (!!user.outlet) networkFilters.dealers = [user.outlet];
        if (!!localStorage.getItem('sso') && !user.isInternal) networkFilters.dealers = this.options.dealers.map(x => x.value)
        this.updateValues(networkFilters, true);
      })
    } else {
      this.updateValues(networkFilters, true);
    }
  }

  setSavedValues(network: Network) {
    this.updateValues(network, true);
  }

  updateValues(networkFilters: NetworkFilters, apply?: boolean) {
    this.formReady = false;
    this.form.patchValue(networkFilters);
    if (networkFilters.brands.includes('NHCAST')) {
      const brands = this._brands.filter((brand) => brand.groupType === 'AG').map((brand) => brand.name);
      this.form.patchValue({ brands: brands });
    } else if (networkFilters.brands.length === 0 && this._brands[0]) {
      this.form.patchValue({ brands: [this._brands[0].name] });
    }
    if (networkFilters.markets.length == 1) {
      forkJoin({
        dealers: this.apiService.getDealers(networkFilters.markets[0], networkFilters.brands, networkFilters.areaSales, networkFilters.areaAfterSales, this._user.dealerGroup ?? null),
        areas: this.apiService.getAreas(networkFilters.markets[0], networkFilters.brands),
      }).subscribe(({ dealers, areas }) => {
        this.setDealerAndAreaOptions(dealers, areas.areaSales, areas.areaAfterSales, false);
        this.formReady = true;
        if (apply) this.applyNetworkFilters.next()
      })
    } else {
      this.formReady = true;
      if (apply) this.applyNetworkFilters.next()
    }
  }

  private formSubscription() {
    this.form.valueChanges.pipe(
      pairwise(),
    ).subscribe(([prev, curr]) => {
      if (this.formReady) {
        this.formReady = false;
        if (curr.regions && curr.brands && !_.isEqual(prev.brands, curr.brands)) {
          this.onBrandChange(curr.brands, curr.regions);
        }
        if (curr.regions && !_.isEqual(prev.regions, curr.regions)) {
          this.onRegionChange(curr.regions)
        }
        if (curr.subregions && !_.isEqual(prev.subregions, curr.subregions)) {
          this.onSubregionChange(curr.subregions);
        }
        if (curr.markets && curr.brands && ((!_.isEqual(prev.markets, curr.markets)) || !_.isEqual(prev.brands, curr.brands))) {
          if (Array.isArray(curr.markets) && curr.markets.length > 0 &&
            Array.isArray(curr.brands) && curr.brands.length > 0) {
            this.onMarketChange(curr.markets, curr.brands);
          }
        }
        if (curr.dealers && !_.isEqual(prev.dealers, curr.dealers)) {
          //do dealers
          let selectedDealers = [...this._dealers];
          if (curr.dealers.length > 0) selectedDealers = selectedDealers.filter((dealer) => (curr.dealers ?? []).includes(dealer.name));
          this.options.outlets = this.createOutletOptionsList(selectedDealers);
        }
        if (curr.outlets && curr.dealers?.length == 0 && !_.isEqual(prev.outlets, curr.outlets)) {
          //do outlets
          this.selectParentDealers(curr.outlets);
        }
        if (curr.areaSales && !_.isEqual(prev.areaSales, curr.areaSales)) {
          let selectedAreas = [...this._areaSales];
          if (curr.areaSales.length > 0) selectedAreas = selectedAreas.filter((area) => (curr.areaSales ?? []).includes(area.code));
          const subareas = selectedAreas.flatMap((area) => area.subAreas);
          this.options.subareaSales = subareas.map((subarea) => this.createOptionsList(subarea.code, subarea.name));
          if (prev.areaSales)
            this.onAreaChange(curr.areaSales, curr.areaAfterSales ?? [], curr.markets ?? [], curr.brands ?? []);

        }
        if (curr.subareaSales && curr.areaSales?.length == 0 && !_.isEqual(prev.subareaSales, curr.subareaSales)) {
          //do subareaSales
          this.selectParentAreaSales(curr.subareaSales);
        }
        if (curr.areaAfterSales && !_.isEqual(prev.areaAfterSales, curr.areaAfterSales)) {
          //do areaAfterSales
          let selectedAreas = [...this._areaAfterSales];
          if (curr.areaAfterSales.length > 0) selectedAreas = selectedAreas.filter((area) => (curr.areaAfterSales ?? []).includes(area.code));
          const subareas = selectedAreas.flatMap((area) => area.subAreas);
          this.options.subareaAfterSales = subareas.map((x) => this.createOptionsList(x.code, x.name));
          if (prev.areaAfterSales)
            this.onAreaChange(curr.areaSales ?? [], curr.areaAfterSales, curr.markets ?? [], curr.brands ?? []);
        }
        if (curr.subareaAfterSales && curr.areaAfterSales?.length == 0 && !_.isEqual(prev.subareaAfterSales, curr.subareaAfterSales)) {
          //do subareaAfterSales
          this.selectParentAreaAfterSales(curr.subareaAfterSales);
        }
        this.formReady = true;
      }
      this.enable(curr);
      this.filterOptions();
    })
    this.form.updateValueAndValidity();
  }

  private setValidators() {
    this.control('regions').setValidators(Validators.required);
    this.control('brands').setValidators([brandTypeValidator(this._brands), Validators.required]);
    if (this._user.dealerGroup || this._user.csiProfile?.startsWith('DEALER')) {
      this.control('dealers').setValidators(Validators.required);
    } else {
      this.control('dealers').clearValidators();
    }
    this.form.updateValueAndValidity();
  }

  private control(control: keyof INetworkFormGroup): FormControl<string[]> {
    return this.form.controls[control];
  }



  private onBrandChange(brands: string[], regions: string[]) {
    const selectedBrands = this._brands.filter((brand) => brands.includes(brand.name));
    const availableRegions = [...new Set(selectedBrands.flatMap(x => x.regions).map(x => x.name))];
    const validRegions = regions.filter(x => availableRegions.includes(x));
    if (!_.isEqual(validRegions, regions) && this.availableFilters.includes('regions')) {
      this.control('regions').setValue(validRegions);
      this.onRegionChange(validRegions);
      // this.filterOptions()
    }
  }

  private onRegionChange(regions: string[]) {
    if (this.availableFilters.includes('subregions')) {
      this.control('subregions').setValue([]);
      this.onSubregionChange([])
    }
    if (regions.length > 1) {
      this.disableControls(
        'areaAfterSales',
        'areaSales',
        'dealers',
        'markets',
        'outlets',
        'subareaAfterSales',
        'subareaSales',
        'subregions'
      );
    } else {
      this.enableControls('subregions', 'markets');
    }
  }

  private onSubregionChange(subregions: string[]) {
    if (this.availableFilters.includes('markets')) {
      this.control('markets').setValue([]);
      this.onMarketChange([], []);
    }
    if (this.availableFilters.includes('regions')) {
      this.selectParentRegions(subregions);
    }
  }

  private onMarketChange(markets: string[], brands: string[]) {
    this.selectParentSubregions(markets);
    if (markets.length == 1) {
      forkJoin({
        dealers: this.apiService.getDealers(markets[0], brands, [], [], this._user.dealerGroup ?? null),
        areas: this.apiService.getAreas(markets[0], brands),
      }).subscribe(({ dealers, areas }) => {
        this.setDealerAndAreaOptions(dealers, areas.areaSales, areas.areaAfterSales, true);
      })
    }
  }

  private onAreaChange(areaSales: string[], areaAfter: string[], markets: string[], brands: string[]) {
    forkJoin({
      dealers: this.apiService.getDealers(markets[0], brands, areaSales, areaAfter, this._user.dealerGroup ?? null),
    }).subscribe(({ dealers }) => {
      this.setDealerOptions(dealers, true);
    })
  }


  private setDealerAndAreaOptions(dealers: Dealer[], areaSales: Area[], areaAfterSales: Area[], clearFilters?: boolean) {
    if (clearFilters) {
      this.formReady = false;
      this.control('dealers').setValue([]);
      this.control('outlets').setValue([]);
      this.control('areaSales').setValue([]);
      this.control('subareaSales').setValue([]);
      this.control('areaAfterSales').setValue([]);
      this.control('subareaAfterSales').setValue([]);
      this.formReady = true;
    }

    // dealers without an area come back with an empty code, shown as "-": they can't be filtered on the BE, so hide them
    areaSales = this.removeEmptyAreas(areaSales);
    areaAfterSales = this.removeEmptyAreas(areaAfterSales);

    this._dealers = dealers;
    this._areaSales = areaSales;
    this._areaAfterSales = areaAfterSales;
    this.options.dealers = this.createDealerOptionsList(dealers);
    this.options.outlets = this.createOutletOptionsList(dealers);
    this.options.areaSales = areaSales.map((area) => this.createOptionsList(area.code, area.name));
    this.options.subareaSales = areaSales
      .flatMap((area) => area.subAreas)
      .map((subarea) => this.createOptionsList(subarea.code, subarea.name));
    this.options.areaAfterSales = areaAfterSales.map((area) => this.createOptionsList(area.code, area.name));
    this.options.subareaAfterSales = areaAfterSales
      .flatMap((area) => area.subAreas)
      .map((subarea) => this.createOptionsList(subarea.code, subarea.name));
  }

  private removeEmptyAreas(areas: Area[]): Area[] {
    const hasCode = (code: string | null | undefined) => !!code && code.trim() !== '' && code.trim().toLowerCase() !== 'null';
    return (areas ?? [])
      .filter((area) => hasCode(area.code))
      .map((area) => ({ ...area, subAreas: (area.subAreas ?? []).filter((subarea) => hasCode(subarea.code)) }));
  }

  private setDealerOptions(dealers: Dealer[], clearFilters?: boolean) {
    if (clearFilters) {
      this.formReady = false;
      this.control('dealers').setValue([]);
      this.control('outlets').setValue([]);
      this.formReady = true;
    }

    this._dealers = dealers;
    this.options.dealers = this.createDealerOptionsList(dealers);
    this.options.outlets = this.createOutletOptionsList(dealers);
  }

  private selectParentSubregions(markets: string[]) {
    const selectedSubregions: string[] = [];
    let subregions: Subregion[] = this._brands
      .flatMap((brand) => brand.regions)
      .flatMap((region) => region.subregions);

    if (this.control('brands').value.length > 0) {
      subregions = this._brands
        .filter(brand => this.control('brands').value.includes(brand.name))
        .flatMap((brand) => brand.regions)
        .flatMap((region) => region.subregions);
    }

    markets.forEach((market) => {
      subregions.filter((sub) => sub.markets.includes(market))
        .map((sub) => sub.name)
        .filter((name, index, self) => self.indexOf(name) === index)
        .forEach((sub) => {
          selectedSubregions.push(sub);
        });
      //selectedSubregions.push((subregions.filter((sub) => sub.markets.includes(market)) as Subregion).name);
    });
    selectedSubregions.push(...this.control('subregions').value);
    this.control('subregions').setValue([...new Set(selectedSubregions)]);
    this.selectParentRegions([...new Set(selectedSubregions)]);
  }

  private selectParentRegions(subregions: string[]) {
    const selectedRegions: string[] = [];
    let brands = [...this._brands];
    if (this.control('brands').value.length > 0) {
      brands = brands.filter((brand) => this.control('brands').value.includes(brand.name));
    }
    const regions: Region[] = brands.flatMap((brand) => brand.regions);
    subregions.forEach((subregion) => {

      regions
        .filter(reg =>
          reg.subregions.flatMap(sub => sub.name).includes(subregion)
        )
        .forEach(reg => selectedRegions.push(reg.name));

      /*selectedRegions.push(
        (regions.find((reg) =>
          reg.subregions.flatMap((sub) =>
            sub.name)
            .includes(subregion)) as Region).name
      );*/
    });
    selectedRegions.push(...this.control('regions').value);
    this.control('regions').setValue([...new Set(selectedRegions)]);
    // this.filterOptions();
  }

  private selectParentDealers(outlets: string[]) {
    const parentDealers = this._dealers
      .filter((dealer) => dealer.outlets.some((out) => outlets.includes(out.name)))
      .map((dealer) => dealer.name);
    this.control('dealers').setValue([...new Set([...parentDealers])]);
  }

  private selectParentAreaSales(subareas: string[]) {
    const parentAreas = this._areaSales
      .filter((area) => area.subAreas.some((sub) => subareas.includes(sub.code)))
      .map((area) => area.code);
    this.control('areaSales').setValue([...new Set([...parentAreas])]);
  }

  private selectParentAreaAfterSales(subareas: string[]) {
    const parentAreas = this._areaAfterSales
      .filter((area) => area.subAreas.some((sub) => subareas.includes(sub.code)))
      .map((area) => area.code);
    this.control('areaAfterSales').setValue([...new Set([...parentAreas])]);
  }

  private  filterOptions() {
    let filteredBrands = [...this._brands];
    this.options.brands = this._brands.map((x) => this.createOptionsList(x.name));

    //For each field we pick the selected elements and then map their children options
    //PROCESS REGIONS
    const brandNames = this.control('brands').value;
    if (brandNames.length > 0) filteredBrands = filteredBrands.filter((brand) => brandNames.includes(brand.name));
    let filteredRegions = filteredBrands.flatMap((brand) => brand.regions);
    let uniqueRegions: Map<string, Region> = new Map();
    filteredRegions.forEach((region) => {
      uniqueRegions.set(region.name, region);
    });
    filteredRegions = [...uniqueRegions.values()];
    this.options.regions = filteredRegions.map((region) => this.createOptionsList(region.name));

    //PROCESS SUBREGIONS
    const regionNames = this.control('regions').value;
    if (regionNames.length > 0) filteredRegions = filteredRegions.filter((region) => regionNames.includes(region.name));
    let filteredSubregions = filteredRegions.flatMap((region) => region.subregions);
    let uniqueSubregions: Map<string, Subregion> = new Map();
    filteredSubregions.forEach((subregion) => {
      uniqueSubregions.set(subregion.name, subregion);
    });
    filteredSubregions = [...uniqueSubregions.values()];
    this.options.subregions = filteredSubregions.map((subregion) => this.createOptionsList(subregion.name));

    //PROCESS MARKETS
    const subregionNames = this.control('subregions').value;
    if (subregionNames.length > 0) filteredSubregions = filteredSubregions.filter((subregion) => subregionNames.includes(subregion.name));
    let filteredMarkets = filteredSubregions.flatMap((subregion) => subregion.markets);
    this.options.markets = [...new Set(filteredMarkets)].map((x) => this.createOptionsList(x));
  }

  private createOptionsList(value: string, name?: string): SelectOption {
    return { name: name ? name : value, value: value };
  }

  private createDealerOptionsList(dealers: Dealer[]): SelectOption[] {
    return dealers.map((dealer) => {
      return { name: `${dealer.fullName} - ${dealer.addrCity} - ${dealer.name}`, value: dealer.name };
    });
  }

  private createOutletOptionsList(dealers: Dealer[]): SelectOption[] {
    const options: SelectOption[] = [];
    dealers.forEach((dealer) => {
      options.push(
        ...dealer.outlets.map((outlet) => {
          return {
            value: outlet.name,
            name: `${outlet.fullName} - ${outlet.addrCity} - ${outlet.name}`,
          };
        })
      );
    });
    return options;
  }

  private disableControls(...control: (keyof NetworkFilters)[]) {
    control.forEach((control) => {
      if (!this.control(control).disabled) {
        // this.control(control).reset([], { emitEvent: false });
        this.control(control).disable({ emitEvent: false });
      }
    });
  }

  private enableControls(...control: (keyof NetworkFilters)[]) {
    control.forEach((control) => {
      if (this.control(control).disabled) this.control(control).enable({ emitEvent: false });
    });
  }

  private enable(value: Partial<INetworkFilters>) {
    {
      if (value.markets?.length === 1) {
        const keys: (keyof NetworkFilters)[] = [
          'outlets',
          'dealers',
          'areaSales',
          'areaAfterSales',
          'subareaAfterSales',
          'subareaSales',
        ];
        let populatedField: keyof NetworkFilters | undefined;
        keys.forEach((key) => {
          if ((value[key]?.length ?? 0) > 0) populatedField = key;
        });
        switch (populatedField) {
          case 'outlets':
            this.enableControls('dealers');
            this.disableControls('areaSales', 'areaAfterSales', 'subareaAfterSales', 'subareaSales');
            break;
          case 'dealers':
            this.enableControls('outlets');
            this.disableControls('subareaAfterSales', 'subareaSales');
            break;
          //case 'areaSales':
          case 'subareaSales':
          //case 'areaAfterSales':
          case 'subareaAfterSales':
            this.disableControls('outlets', 'dealers');
            this.enableControls('areaSales', 'areaAfterSales', 'subareaAfterSales', 'subareaSales');
            break;
          default:
            this.enableControls(
              'outlets',
              'dealers',
              'areaSales',
              'areaAfterSales',
              'subareaAfterSales',
              'subareaSales'
            );
        }
      } else {
        this.disableControls(
          'outlets',
          'dealers',
          'areaSales',
          'areaAfterSales',
          'subareaAfterSales',
          'subareaSales'
        );
      }
    }
  }
}
